import { useNavigate } from 'react-router'

import type { ModuleRegistryRow } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { MODULE_REGISTRY } from '@ctcells/fixtures'
import { t } from '@ctcells/i18n'

import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { StaleMark } from '../../atoms/StaleMark.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { ADMIN_TABS, type AdminTabId } from '../admin/adminTabs.ts'
import { dateTimeText, staleValue } from '../format.ts'
import { useGroupTabLayout } from '../groupTabs.ts'
import type { ScreenAccess } from '../access.ts'
import { RowLink } from '../resourceParts.tsx'
import { withPriority } from '../listColumnPriority.ts'

import { coarseModuleStateDetail, coarseModuleStateLabel } from './coarseModuleState.ts'
import { moduleName } from './moduleRows.ts'

import '../screens.css'
import '../resource.css'

/**
 * 모듈 레지스트리 — 와이어프레임 `modules()` 2615행.
 *
 * 🔴 **뺀 것을 되살리지 마라**(§1-4 · eric 지시):
 *    - 상단 KPI 4칸 없음 (*"4개 밖에 없어서 의미가 없어"*)
 *    - 검색 · 필터 툴바 없음
 *    - 「+ 모듈 추가」 · 「제거」 버튼 없음 (*"제거.. 라는 버튼은 없애"*)
 *    - 「자동/수동」(주소 출처) 열 없음 (*"수동 자동을 빼라고 ip 주소를 빼란는게 아니라"*)
 *    - 탭이 하나뿐이라 탭 줄도 없다
 *
 * 🔴 **열은 `ModuleSnapshot` 응답에 있는 값으로만 짠다**(2026-09-20 eric 지시 —
 *    「왜 디비를 보고 화면을 만들었어? 저 응답에 맞게 화면을 구성해」).
 *    `IP 주소` · `응답 확인` · `최근 진단` · `보정` · `버전` 열을 뺐다 — 응답에 없거나(앞의 넷)
 *    늘 비어 있었다(보정). **되살리려면 계약부터 세워라.**
 *    열별 출처표는 `moduleRows.ts` 상단에 있다 — **그 표가 이 화면의 진실이다.**
 */
/**
 * 모듈 상세로 가는 곳. 🔴 **첫 칸 링크와 행 클릭이 같은 함수를 쓴다** — 경로 문자열을 두 벌
 * 적으면 한쪽만 고쳐져 누르는 자리마다 다른 데로 간다.
 */
const moduleDetailPath = (m: ModuleRegistryRow): string => `${PATHS.modules}/${m.module_id}`

/**
 * 모듈 연결 칩 — 목록 「통신」 열(eric 2026-10-30) · 상세 제목 오른쪽 한 벌(eric 2026-10-22 「모듈 이름 옆에다가 칩으로」 · 「타이틀 (어셈블러 오른쪽) 오른쪽에 칩으로」).
 *
 * - 🔴 값은 `stale`(서버 판정 · 계약 필수 불리언)뿐이다. 화면이 시간을 재서 만들지 않는다 — 「모름」 갈래가 없다
 * - 연결 = 상태 알약(`StatusBadge`) — 사용자 「활성」과 같은 초록(`ok`) · 응답 없음 = 낡음 표지(`StaleMark` · ◌ + 점선) — 모니터링 ·
 *   소모품 판의 「응답 없음」과 같은 꼴이다. 새 칩을 만들지 않았다
 * - 🔴 색만으로 말하지 않는다 — 글자(연결 · 응답 없음)와 글리프가 같이 선다
 * - 「연결」은 테 없는 알약(`flat` · eric 2026-10-30) · 「응답 없음」은 바탕이 없어 점선 테가 곧 칩의 경계라 그대로 둔다
 */
export function ModuleLinkChip({ stale }: { stale: boolean }) {
  return stale ? (
    <StaleMark label={t('safety.moduleStale')} />
  ) : (
    // 테 없는 알약 — 초록 면이 있어 테가 없어도 칩이 선다(eric 2026-10-30 「관리 모듈 연결 칩도 테두리 없애줘」) · 「응답 없음」은 바탕 없는 점선 표지라 테 그대로
    <StatusBadge flat label={t('module.connected')} status="IDLE" tone="ok" />
  )
}

export interface ModulesScreenProps {
  /**
   * 열린 관리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`admin/adminTabs.ts`).
   * **안 넘기면 무리 탭 줄을 안 그린다**(규칙은 `useGroupTabLayout` 주석) — 장비 관점 콘솔이
   * 그렇게 쓴다. 거기선 모듈·로그가 사이드바 「시스템」에 각자 서 있어서 탭 줄이 또 뜨면 안 된다.
   */
  adminTab?: AdminTabId | undefined
  /**
   * 오른쪽 판(서랍)에 열린 모듈. 그 행을 «고른 행»으로 그린다(채움 + `aria-selected`).
   * 🔴 **화면이 주소를 읽지 않는다** — 라우트가 넘긴다. 장비 관점 콘솔은 안 넘기므로 고른 행이 없다.
   */
  selectedModuleId?: string | undefined
  modules?: readonly ModuleRegistryRow[]
  access?: ScreenAccess
  loading?: boolean
  /*
   * 🔴 「종착 포함」 토글 · 종착 행(흐림 · 「서비스 종착」) 갈래는 없다(eric 2026-10-22 「종착 포함 이거 버튼을 없애줘」) —
   *    목록은 늘 운용 중 모듈만 받는다(컨테이너 `activeModules`)
   */
}

export function ModulesScreen({
  adminTab,
  selectedModuleId,
  modules = MODULE_REGISTRY,
  access = {},
  loading = false,
}: ModulesScreenProps) {
  /*
   * 🔴 관리 무리의 탭 줄. 켜지면 관리·로그와 **같은 줄**을 쓴다(2026-09-18 eric 지시).
   * ⚠ 이 화면은 자체 탭이 없어서 무리 탭을 끄면 탭 줄 자체가 사라진다 — `v0.1.0` 모양 그대로다.
   */
  const groupTabs = useGroupTabLayout(ADMIN_TABS, adminTab, 'modules', access)
  const navigate = useNavigate()
  const columns: Column<ModuleRegistryRow>[] = [
    {
      key: 'name',
      header: t('column.name'),
      nowrap: true,
      sort: 'text',
      sortValue: (m) => moduleName(m),
      /*
       * 🔴 **이름은 `module_type` 이 만든다 — `module_id` 에서 읽어내지 않는다**(2026-09-21).
       *    종전에는 `moduleLabel(m.module_id as ModuleId)` 갈래가 있었는데, 실물 ID 는
       *    `centrifuge-1` 꼴이라 사전에 없어 **원값이 그대로 이름 자리에 섰다.**
       *    ID 꼴은 계약이 정한 게 아니라 등록할 때 사람이 붙인 이름이다.
       * ⚠ **표의 한 칸이라 모르는 종류는 `—`** 다 — 칸이 비면 열이 어긋난다. 어느 대인지는
       *   「모듈 ID」 열이 말한다.
       * 🔴 **`display_name`(사람이 붙인 이름)이 있으면 그것이 먼저다**(2026-09-26 응답 칸).
       *    없으면 종전대로 종류 라벨이다 — 계약이 「없을 때 무엇을 보일지는 보는 쪽이 정한다」고 했다.
       */
      /*
       * 🔴 **이름 칸에는 이름만 선다 — 연결 칩은 바로 옆 「통신」 열이다**(eric 2026-10-30 「통신 컬럼을 하나 더 두고..
       *    지금은 연결이 바로 옆에 있잖아」). 10-22 의 「이름 오른쪽 칩」을 뒤집었다. 상세 제목 오른쪽 칩은 그대로다
       */
      cell: (m) => <RowLink to={moduleDetailPath(m)}>{moduleName(m)}</RowLink>,
    },
    {
      key: 'link',
      header: t('column.link'),
      nowrap: true,
      // 보이는 글자 순(연결 · 응답 없음) — 응답 없는 모듈끼리 모인다
      sort: 'text',
      sortValue: (m) => (m.stale ? t('safety.moduleStale') : t('module.connected')),
      // 값은 `stale`(서버 판정)뿐이다 — 상세 제목 칩과 한 벌(`ModuleLinkChip`)
      cell: (m) => <ModuleLinkChip stale={m.stale} />,
    },
    /*
     * 🔴 「제어 소유」 · 「알람」 열은 없다(eric 2026-10-22 「이거... 알람 제어 소유 빼줘..」). 제어 소유는 상세 서랍 「연결」 카드 줄에 남는다
     */
    {
      key: 'state',
      header: t('column.status'),
      nowrap: true,
      // 칸이 보이는 굵은 표기 그대로 글자 순이다(세부 상태가 아니라)
      sort: 'text',
      sortValue: (m) => coarseModuleStateLabel(m.state),
      /*
       * 🔴 서버가 준 상태를 **굵은 표기**(대기 · 진행 · 개입 대기 · 오류 · 복구 중 · 안전정지)로 접는다
       *    (eric 2026-09-23 「디테일한 걸로 하지 말고 대기 · 진행 이런 식으로.. 수시로 바뀌게 하지 마」).
       *    세부 동작(이동 중 · 흡입 중 …)은 툴팁으로 내려가고 모듈 상세에서 본다. 접는 표는
       *    `coarseModuleState.ts` — 정본 6분류를 그대로 쓰고 새 분류를 만들지 않는다.
       * ⚠ **사전에 없으면 원값이 그대로 나온다**(`enumLabel` 규약) — `—` 도 「대기」도 아니다.
       *   「값이 없다」(`—`)와 「모르는 값을 받았다」(원값)는 다른 사실이고, 원값이 화면에
       *   보이는 것이 **사전이 뚫렸다는 경보**다.
       */
      /*
       * 🔴 **응답 없는 모듈의 상태는 «마지막 값»이다**(계약 `stale` — 「마지막 값을 현재로 보이지
       *    않는다」). 투명도로 흐리지 않고 글자로 말한다(「대기 · 마지막 값」 · `staleValue`).
       */
      cell: (m) => (
        <StatusBadge
          label={staleValue(coarseModuleStateLabel(m.state), m.stale)}
          originalValue={coarseModuleStateDetail(m.state)}
          status={m.state}
        />
      ),
    },
    {
      key: 'moduleId',
      header: t('column.moduleId'),
      nowrap: true,
      sort: 'text',
      sortValue: (m) => m.module_id,
      // 🔴 같은 종류가 여럿이다(2026-09-20 실측 8건). 이름만으로는 어느 대인지 알 수 없다
      cell: (m) => <span className="mono">{m.module_id}</span>,
    },
    /*
     * 🔴 「안전」 열은 없다(eric 2026-10-22 「안전은 빼줘」). 마지막 응답은 맨 끝이다(같은 날 「마지막 응답은.. 마지막에 넣고」).
     *    열은 이름 | 통신 | 상태 | 모듈 ID | 마지막 응답(eric 2026-10-30)
     */
    {
      key: 'lastSeen',
      header: t('row.lastSeen'),
      nowrap: true,
      sort: 'time',
      sortValue: (m) => m.last_seen,
      // 🔴 **상대 시간이 아니라 시각이다.** 「4초 전」은 화면이 타이머를 돌려야 하고,
      //    그러면 서버가 말하지 않은 값을 프론트가 계속 만들어 내게 된다
      cell: (m) => <span className="muted mono">{dateTimeText(m.last_seen)}</span>,
    },
  ]

  return (
    <ListPage
      title={groupTabs ? t('screen.admin.title') : t('screen.modules.title')}
      {...(groupTabs
        ? {
            onTabChange: groupTabs.onChange,
            tabValue: groupTabs.value,
            tabs: groupTabs.tabs,
            tabsLabel: t('screen.admin.title'),
          }
        : {})}
    >
      {/*
        🔴 **「연결 끊김 — 제어 불가」 알림을 여기 두지 않는다**(2026-09-18 eric 지시 —
           "모듈에서 아직도 끊김 제어불가가 떴다가 사라져.. 그거 안보이게 해").
           구독이 붙기 전 한 프레임 stale 로 판정돼 **떴다가 사라지는 깜빡임**이 났다.
        ⚠ **빠진 것은 표시뿐이다. 동작은 그대로다** — `gate()` 가 `access.controlDisabled` 를 보고
           상태변경 버튼을 계속 전부 비활성 + 사유(`reason.streamStale`)로 둔다(§6 규약 유지).
           스크린리더가 읽는 사유도 버튼에 그대로 붙어 있다.
        ⚠ `OfflineNotice` 자체를 지우지 마라 — 다른 화면 16곳이 쓴다. **모듈 3화면만** 뺀 것이다.
      */}
      <DataTable
        caption={t('screen.modules.title')}
        /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
        columns={withPriority('modules', columns)}
        /*
          🔴 다섯 열이 남는 폭을 똑같이 나눈다(eric 2026-10-30 「컬럼 너비를 맞춰줘」) — 자동 배분이면 이름 · 모듈 ID 가
             넓게 받고 칩 하나인 통신 · 상태가 좁게 붙는다. 작업 목록과 같은 `even` 이다
        */
        columnWidths="even"
        /* 처음 차례는 모듈 ID 오름차순 — 서버 차례와 같다(`services/modules.py` `order_by(module_id)`) */
        defaultSort={MODULES_DEFAULT_SORT}
        /*
          🔴 **행 강조를 쓰지 않는다**(2026-09-18 eric 지시 — "왜 왼쪽에.. 검은 줄이 있어?").
             예전에는 `emphasis={(m) => m.stale}` 였는데, 실물에서 **모든 모듈이 `stale: true`** 라
             전 행에 띠가 붙었다. **전부를 강조하면 강조가 아니다** — 로그·작업 표처럼 몇 줄만
             걸릴 때만 뜻이 있다. 연결 상태는 「응답 없음」 문구가 이미 말하므로 정보도 안 사라진다.
             모듈이 늘어 일부만 stale 이 되더라도 되살리지 마라. 표 모양은 다른 목록과 같아야 한다.
        */
        emptyLabel={t('state.empty.generic')}
        loading={loading}
        /*
          🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23). 첫 칸 `RowLink` 는 그대로다 —
             키보드로 가는 길은 그 버튼뿐이다(`DataTable.onRowClick` 주석).
        */
        onRowClick={(m) => void navigate(moduleDetailPath(m))}
        rowKey={(m) => m.module_id}
        rows={modules}
        /* 🔴 고른 행은 색만으로 말하지 않는다 — `DataTable` 이 `aria-selected` 를 같이 건다 */
        selected={(m) => m.module_id === selectedModuleId}
      />
    </ListPage>
  )
}

/** 모듈 표의 처음 정렬 — 모듈 ID 오름차순(서버 차례) */
const MODULES_DEFAULT_SORT: TableSort = { key: 'moduleId', direction: 'asc' }
